import _ from 'lodash';
import { isFromMarketingCampaign } from '@/views/config/coupon/helper/utils';
import { PRO_PLUS_SOFT_BUNDLE_CODES as allowedBundleCodes } from '@/views/config/soft-bundle/constant';
import { getBundlePlanCodeListFromPartnerConfig } from '@/common/service/parse/bundle-parser';
import store from '@/store';
import { planTypes } from '@/common/constants';

/**
 * Persistent cart condition strategies
 * v1: Original logic - disable cart for all marketing campaigns
 * v2: Enhanced logic - enable cart for specific bundle codes (pro_zsu, pro_zuu, pro_zau)
 */
const persistentCartStrategies = {
  /**
   * V1 Strategy: Original persistent cart logic
   * - Disable if feature toggle is off
   * - Disable for all marketing campaign links
   */
  v1: (featureEnabled) => {
    if (!featureEnabled) {
      return false;
    }

    // from marketing link, should empty the shopping cart
    if (isFromMarketingCampaign()) {
      return false;
    }

    return true;
  },

  /**
   * V2 Strategy: Enhanced persistent cart logic with bundle support
   * - Disable if feature toggle is off
   * - Enable for specific jointsale bundle codes (pro_zsu, pro_zuu, pro_zau)
   * - Disable for other marketing campaign links
   */
  v2: (featureEnabled) => {
    if (!featureEnabled) {
      return false;
    }

    // not from persistent cart, should disable persist shopping cart
    if (!window.$context?.is.fromPersistentCart) {
      return false;
    }

    // Check if jointsale parameter is one of the allowed bundle codes
    const jointsaleCode = window.$config('jointsale');
    if (jointsaleCode && allowedBundleCodes.includes(jointsaleCode)) {
      return true;
    }

    // from marketing link, should empty the shopping cart
    if (isFromMarketingCampaign()) {
      return false;
    }

    return true;
  }
};

export const persistCartConstant = Object.defineProperties({}, {
  persistShoppingBagSwitch: {
    get() {
      // from backend feature toggle
      return window.$isEnabled('displayPersistentCart');
    }
  },
  isShowPersistentCartV2: {
    get() {
      return window.$isEnabled('displayPersistentCartV2');
    }
  },
  enablePersistShoppingBag: {
    get() {
      const v1Enabled = persistCartConstant.persistShoppingBagSwitch;
      const v2Enabled = persistCartConstant.isShowPersistentCartV2;

      // Priority: V2 > V1
      if (v2Enabled) {
        return persistentCartStrategies.v2(v2Enabled);
      }

      if (v1Enabled) {
        return persistentCartStrategies.v1(v1Enabled);
      }

      return false;
    }
  },
  enablePersistShoppingBagForV2: {
    get() {
      return persistCartConstant.enablePersistShoppingBag && persistCartConstant.isShowPersistentCartV2;
    }
  },
  enablePersistShoppingBagForV1: {
    get() {
      // v2 is enabled, v1 should be disabled (v2 priority is higher than v1)
      if (persistCartConstant.isShowPersistentCartV2) {
        return false;
      }

      return persistCartConstant.enablePersistShoppingBag && persistCartConstant.persistShoppingBagSwitch;
    }
  },
  fromPersistentCart: {
    get() {
      return persistCartConstant.enablePersistShoppingBag && window.$context?.is?.fromPersistentCart;
    }
  }
});

const continueShopping = () => {
  const referrer = document.referrer || '';
  if (referrer.includes?.('/pricing')) {
    return window.location.replace(referrer);
  }

  window.location.replace('/pricing');
};

const addShoppingBagPersistEventListener = () => {
  window.addEventListener('billingShoppingBagInstanceReady', () => {
    window.billingShoppingBagInstance?.updateOptions({
      clickShoppingBag: () => {
        if (window.$context?.is.checkoutPage) {
          window.$context?.$emit('plan-edit-action');
        }
      },
    });
  });
};

const removePersistShoppingBagWhenInVisible = () => {
  if (!persistCartConstant.enablePersistShoppingBag) {
    const element = document.querySelector('#new_buyflow_header_container .billing-shopping-bag__container');
    element && element.remove();
  }
};

const initShoppingBagAction = () => {
  removePersistShoppingBagWhenInVisible();
  if (!persistCartConstant.isShowPersistentCartV2) {
    addShoppingBagPersistEventListener();
  }
};

export const getPersistShoppingBagLink = () => {
  try {
    const currentURL = new URL(window.location.href);
    // force replace current pathName to /opc/buy, avoid customer redirect to checkout page when the customer click the shopping cart
    currentURL.pathname = '/opc/buy';
    return currentURL.toString();
  } catch (e) {
    return window.location.href;
  }
};

export function formatPersistInfoFromShoppingCart(persistInfo = {}, {
  partnerRefer = {}
}) {
  let bundlePlanCodeList = [];
  let bundleCode = '';

  if (partnerRefer?.features?.useInSoftBundle) {
    bundlePlanCodeList = getBundlePlanCodeListFromPartnerConfig(partnerRefer);
    bundleCode = partnerRefer?.jointsaleCode;
  }


  const result = _.mapValues(persistInfo, (item, planCode) => {
    const isSoftBundle = bundlePlanCodeList.includes(planCode);
    const { selected_number: selectNumber, ...rest } = item || {};

    return {
      ...rest,
      planCode,
      selectNumber,
      extra: {
        ...(isSoftBundle ? { bundleCode } : {}),
      }
    };
  });

  const ordered = Object.fromEntries(
    _.sortBy(Object.entries(result), ([planCode]) => {
      const map = store.getters.groupByPlanCodePlanMap;
      const plan = map[planCode];
      const type = plan?.type;
      const planType = planTypes[type];
      const index = store.getters.draftPlanFilterKeys.indexOf(planType);
      return index;
    })
  )

  return ordered;

}


export default {
  addShoppingBagPersistEventListener,
  continueShopping,
  removePersistShoppingBagWhenInVisible,
  initShoppingBagAction,
};
